import React from "react";
import { motion } from "framer-motion";

const features = [
  {
    title: "Quotex Signals",
    desc: "Self-learning AI engine surfaces high-probability OTC binary setups across major Quotex pairs in real time.",
    accent: "01",
    iconPath: "M3 12c0-4.97 4.03-9 9-9s9 4.03 9 9-4.03 9-9 9-9-4.03-9-9zm9-4l2.5 4-2.5 4-2.5-4L12 8z",
  },
  {
    title: "Precision Entries",
    desc: "Structured UP / DOWN windows with second-perfect timing, so you enter Quotex with intention, not reaction.",
    accent: "02",
    iconPath: "M3 12h13M13 6l6 6-6 6",
  },
  {
    title: "VIP Sessions",
    desc: "Live coordinated trading rooms with curated binary entry windows for serious Quotex operators.",
    accent: "03",
    iconPath: "M4 6h16M4 12h10M4 18h16",
  },
  {
    title: "Liquidity Levels",
    desc: "OTC liquidity mapping reveals where institutional flow is actually positioned on Quotex pairs.",
    accent: "04",
    iconPath: "M3 18c3-6 6 0 9-3s6 3 9 0",
  },
  {
    title: "Smart Compounding",
    desc: "Adaptive position sizing that grows the account on green days and protects on red ones — built for binary.",
    accent: "05",
    iconPath: "M4 20V8m0 0l-3 3m3-3l3 3M20 4v12m0 0l-3-3m3 3l3-3",
  },
  {
    title: "Risk Control",
    desc: "Strict per-trade exposure and daily drawdown rules baked into every binary signal we release.",
    accent: "06",
    iconPath: "M12 3l8 4v6c0 4.97-3.58 8-8 8s-8-3.03-8-8V7l8-4z",
  },
  {
    title: "Daily Signals",
    desc: "Consistent flow of clean, high-conviction Quotex setups every active OTC session — no noise.",
    accent: "07",
    iconPath: "M12 2v4m0 12v4M2 12h4m12 0h4M5 5l3 3m8 8l3 3M5 19l3-3m8-8l3-3",
  },
  {
    title: "Fast Alerts",
    desc: "Low-latency push directly to Telegram, so you never miss a UP / DOWN entry on Quotex.",
    accent: "08",
    iconPath: "M13 2L3 14h8l-1 8 11-14h-8l0-6z",
  },
];

const FeatureCard = ({ f, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1], delay: (i % 4) * 0.06 }}
    whileHover={{ y: -4 }}
    className="group relative overflow-hidden rounded-[22px] glass-panel p-7 md:p-8 transition-colors duration-500"
    data-testid={`feature-card-${i}`}
  >
    {/* hover gradient */}
    <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-[radial-gradient(circle_at_30%_0%,rgba(78,168,255,0.18),transparent_60%)]" />
    {/* corner glow */}
    <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-[#4ea8ff]/10 blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-700" />

    <div className="relative flex items-start justify-between">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-md">
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="#cfe6ff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
          <path d={f.iconPath} />
        </svg>
      </div>
      <span className="font-mono-luxe text-[10px] tracking-[0.3em] text-white/30">{f.accent}</span>
    </div>

    <h3 className="relative mt-7 font-display text-[22px] md:text-[24px] tracking-tight text-white">
      {f.title}
    </h3>
    <p className="relative mt-3 text-[14px] leading-relaxed text-white/55 max-w-[280px]">
      {f.desc}
    </p>
  </motion.div>
);

const WhyBigBull = () => {
  return (
    <section id="why" className="relative py-32 md:py-40">
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="glow-blob top-[20%] left-[-10%] h-[420px] w-[420px] bg-[rgba(31,111,224,0.10)]" />
        <div className="glow-blob bottom-[10%] right-[-8%] h-[460px] w-[460px] bg-[rgba(78,168,255,0.08)]" />
      </div>

      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-[820px]"
        >
          <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.32em] uppercase text-white/40">
            <span className="h-px w-8 bg-white/20" /> Why BIGBULL
          </span>
          <h2 className="mt-6 font-display text-[42px] sm:text-[56px] md:text-[72px] leading-[1] tracking-[-0.03em] text-balance">
            <span className="text-gradient-silver">Built for binary traders who</span>{" "}
            <span className="text-gradient-blue">refuse to guess.</span>
          </h2>
          <p className="mt-7 max-w-[560px] text-[16px] md:text-[17px] text-white/55 font-light">
            Eight modular systems working in concert — engineered to deliver clean
            structure, real edge, and institutional discipline to every Quotex session.
          </p>
        </motion.div>

        <div className="mt-16 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {features.map((f, i) => (
            <FeatureCard f={f} i={i} key={f.title} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyBigBull;
